// Configuração de Taxas (Transferência e Cobrança)
// Lê as configurações das variáveis de ambiente

/**
 * Valida se um endereço Ethereum é válido
 * @param {string} address - Endereço a ser validado
 * @returns {boolean}
 */
function isValidEthereumAddress(address) {
  return /^0x[a-fA-F0-9]{40}$/.test(address);
}

// ─── Taxa de Transferência ───────────────────────────────────────────────────

/**
 * Percentual da taxa de transferência (0-100).
 * Aplicada em todas as transferências (modo Enviar) e como componente no Receber.
 * Valor padrão: 0 (sem taxa).
 */
const rawFeePercent = import.meta.env.VITE_FEE_PERCENT;
const parsedFeePercent = parseFloat(rawFeePercent);
export const FEE_PERCENT =
  !isNaN(parsedFeePercent) && parsedFeePercent >= 0 && parsedFeePercent <= 100
    ? parsedFeePercent
    : 0;

// ─── Taxa de Cobrança (serviço) ─────────────────────────────────────────────

/**
* Percentual adicional cobrado pelo serviço de recebimento (0-100).
   * Aplicada apenas no modo Receber, somada à taxa de transferência.
   * Valor padrão: 0 (sem taxa de recebimento).
 */
const rawBillingPercent = import.meta.env.VITE_FEE_BILLING_PERCENT;
const parsedBillingPercent = parseFloat(rawBillingPercent);
export const FEE_BILLING_PERCENT =
  !isNaN(parsedBillingPercent) && parsedBillingPercent >= 0 && parsedBillingPercent <= 100
    ? parsedBillingPercent
    : 0;

// ─── Endereço de destino das taxas (Testnet vs Mainnet) ───────────────────────

/**
 * ChainIds considerados testnet (Base Sepolia, Ethereum Sepolia).
 * Usado para escolher VITE_FEE_ADDRESS_TESTNET ou VITE_FEE_ADDRESS_MAINNET.
 */
const TESTNET_CHAIN_IDS = ['0x14a34', '0xaa36a7'];

/**
 * Endereço que recebe as taxas em redes testnet.
 */
const rawFeeAddressTestnet = import.meta.env.VITE_FEE_ADDRESS_TESTNET || '';
export const FEE_ADDRESS_TESTNET = isValidEthereumAddress(rawFeeAddressTestnet) ? rawFeeAddressTestnet : null;

/**
 * Endereço que recebe as taxas em redes mainnet (EVM reais).
 */
const rawFeeAddressMainnet = import.meta.env.VITE_FEE_ADDRESS_MAINNET || '';
export const FEE_ADDRESS_MAINNET = isValidEthereumAddress(rawFeeAddressMainnet) ? rawFeeAddressMainnet : null;

/**
 * Fallback legado: se apenas VITE_FEE_ADDRESS estiver definido, usa para ambas as redes.
 */
const rawFeeAddressLegacy = import.meta.env.VITE_FEE_ADDRESS || '';
const FEE_ADDRESS_LEGACY = isValidEthereumAddress(rawFeeAddressLegacy) ? rawFeeAddressLegacy : null;

/**
 * Retorna o endereço de taxa para a rede indicada por chainId.
 * Testnet (Base Sepolia, Sepolia) → VITE_FEE_ADDRESS_TESTNET;
 * Demais redes → VITE_FEE_ADDRESS_MAINNET.
 * Se só um estiver configurado, usa o disponível; fallback para VITE_FEE_ADDRESS se definido.
 * @param {string|number|null|undefined} chainId
 * @returns {string|null}
 */
export function getFeeAddress(chainId) {
  if (chainId == null || chainId === '') return FEE_ADDRESS_LEGACY;
  const normalized = String(chainId).trim().toLowerCase();
  const hex = normalized.startsWith('0x') ? normalized : '0x' + parseInt(chainId, 10).toString(16);
  const isTestnet = TESTNET_CHAIN_IDS.includes(hex);
  if (isTestnet && FEE_ADDRESS_TESTNET) return FEE_ADDRESS_TESTNET;
  if (!isTestnet && FEE_ADDRESS_MAINNET) return FEE_ADDRESS_MAINNET;
  return FEE_ADDRESS_LEGACY || (isTestnet ? FEE_ADDRESS_TESTNET : FEE_ADDRESS_MAINNET);
}

/** @deprecated Use getFeeAddress(chainId). Mantido para compatibilidade. */
export const FEE_ADDRESS = FEE_ADDRESS_MAINNET || FEE_ADDRESS_TESTNET || FEE_ADDRESS_LEGACY;

// ─── Habilitação ────────────────────────────────────────────────────────────

/**
 * Taxa habilitada se pelo menos uma das taxas > 0 e algum endereço (testnet ou mainnet) válido.
 */
export const IS_FEE_ENABLED =
  (FEE_PERCENT > 0 || FEE_BILLING_PERCENT > 0) &&
  (FEE_ADDRESS_MAINNET !== null || FEE_ADDRESS_TESTNET !== null || FEE_ADDRESS_LEGACY !== null);

// ─── Valor mínimo que pode ser Enviado ou Cobrado (em USD) ───────────────────

/**
* Valor mínimo em USD para operações de Enviar e Receber.
   * Abaixo desse valor o formulário não permite enviar/gerar link para receber.
 * Não afeta o cálculo das taxas (sempre por percentual).
 */
const rawMinAmount = import.meta.env.VITE_FEE_MIN_AMOUNT;
const parsedMinAmount = parseFloat(rawMinAmount);
export const FEE_MIN_AMOUNT = !isNaN(parsedMinAmount) && parsedMinAmount >= 0 ? parsedMinAmount : 0;

// ─── Limite de acúmulo de taxa pendente ─────────────────────────────────────

/**
 * Valor máximo (em USD) de taxa pendente que o usuário pode acumular
 * antes da aplicação exigir o pagamento via popup.
 * Se 0 ou não definido, exige pagamento imediato (comportamento anterior).
 */
const rawAccumulationLimit = import.meta.env.VITE_FEE_ACCUMULATION_LIMIT;
const parsedAccumulationLimit = parseFloat(rawAccumulationLimit);
export const FEE_ACCUMULATION_LIMIT =
  !isNaN(parsedAccumulationLimit) && parsedAccumulationLimit >= 0 ? parsedAccumulationLimit : 0;

// ─── Exportação consolidada ─────────────────────────────────────────────────

export const FEE_CONFIG = {
  percent: FEE_PERCENT,
  billingPercent: FEE_BILLING_PERCENT,
  getFeeAddress,
  address: FEE_ADDRESS,
  enabled: IS_FEE_ENABLED,
  minSendChargeAmount: FEE_MIN_AMOUNT,
  accumulationLimit: FEE_ACCUMULATION_LIMIT,
};

export default FEE_CONFIG;
